import i18n from 'src/locales';
import {
  ActionStepList,
  ButtonsStepFields,
  Canvas,
  MiniAutoflowPolicyStepFields,
  Step,
} from 'src/types/workflowBuilderAPITypes';
import { dynamicCardSelectedCase } from 'src/utils/constants';
import { StepTypes } from 'src/utils/enums';

/**
 * Check if a step type is a terminal step (GO_TO_COMPONENT, GO_TO_INTENT,
 * GO_TO_INTENT_EMAIL, or RESTART_CONVERSATION).
 * Terminal steps cannot have additional steps added after them.
 */
export const isTerminalStep = (stepType: string | undefined): boolean => {
  return (
    stepType === StepTypes.GO_TO_COMPONENT ||
    stepType === StepTypes.GO_TO_INTENT ||
    stepType === StepTypes.GO_TO_INTENT_EMAIL ||
    stepType === StepTypes.RESTART_CONVERSATION
  );
};

export interface StepValidationResult {
  canAddStep: boolean;
  message?: string;
}

/**
 * Validates if a step can be added after a given step
 * Centralized validation logic used by both drag-drop and workflow status
 */
export const validateCanAddStepAfter = (
  step: Step,
  stepId: string,
  canvas: Canvas,
  outputVariableValues: Record<string, Record<string, string>>,
  selectedActionCases: Record<string, boolean>,
  selectedConditions: Record<string, number>,
): StepValidationResult => {
  const { canvas_action_id_to_action_component, step_map } = canvas;

  // Check if step is a terminal step
  if (isTerminalStep(step.step_type)) {
    const message =
      step.step_type === StepTypes.GO_TO_COMPONENT
        ? i18n.t(
            'autoflow-v2-editor:workflowNavigationUtils.noAdditionalStepGoToComponent',
          )
        : step.step_type === StepTypes.GO_TO_INTENT
          ? i18n.t(
              'autoflow-v2-editor:workflowNavigationUtils.noAdditionalStepRoutedIntent',
            )
          : step.step_type === StepTypes.GO_TO_INTENT_EMAIL
            ? i18n.t(
                'autoflow-v2-editor:workflowNavigationUtils.noAdditionalStepRoutedEmailIntent',
              )
            : i18n.t(
                'autoflow-v2-editor:workflowNavigationUtils.noAdditionalStepRestarted',
              );
    return {
      canAddStep: false,
      message,
    };
  }

  // Check if step is a terminal action
  const isTerminalAction = Object.values(
    canvas_action_id_to_action_component,
  ).some(action => action.entry_step_id === stepId && action.is_last_element);
  if (isTerminalAction) {
    return {
      canAddStep: false,
      message: i18n.t(
        'autoflow-v2-editor:workflowNavigationUtils.terminalStepSet',
      ),
    };
  }

  // Use canAddStepAfter to check branching logic
  const canAdd = canAddStepAfter({
    canvasActionIdToActionComponent: canvas_action_id_to_action_component,
    nextStepId: null,
    outputVariableValues,
    selectedActionCases,
    selectedConditions,
    step,
    stepId,
    stepMap: step_map,
  });

  if (!canAdd) {
    return {
      canAddStep: false,
      message: i18n.t(
        'autoflow-v2-editor:workflowNavigationUtils.selectConditionOrButton',
      ),
    };
  }

  return { canAddStep: true };
};

export interface DropValidationResult {
  canDrop: boolean;
  message?: string;
}

interface ValidateStepDropParams {
  canvas: Canvas | undefined;
  isEntryStep: boolean;
  outputVariableValues: Record<string, Record<string, string>>;
  parentStepId: string | null;
  selectedActionCases: Record<string, boolean>;
  selectedConditions: Record<string, number>;
}

/**
 * Validates if a step can be dropped at the current position
 * Returns validation result with canDrop flag and optional error message
 */
export const validateStepDrop = ({
  canvas,
  isEntryStep,
  outputVariableValues,
  parentStepId,
  selectedActionCases,
  selectedConditions,
}: ValidateStepDropParams): DropValidationResult => {
  if (!canvas || !parentStepId) {
    // Entry position or no canvas - allow drop
    return { canDrop: true };
  }

  // If dropping at entry position, always allow
  if (isEntryStep) {
    return { canDrop: true };
  }

  // Get the parent step we're trying to drop after
  const parentStep = canvas.step_map[parentStepId];
  if (!parentStep) {
    return { canDrop: true };
  }

  // Use shared validation logic
  const validation = validateCanAddStepAfter(
    parentStep,
    parentStepId,
    canvas,
    outputVariableValues,
    selectedActionCases,
    selectedConditions,
  );

  return {
    canDrop: validation.canAddStep,
    message: validation.message,
  };
};

/**
 * Check if a step is an internal action step (part of action but not the entry step).
 * Internal action steps should not have UI interactions like edit menu or "create step" lines.
 */
export const isInternalActionStep = (
  stepId: string,
  canvasActionIdToActionComponent?: Record<string, ActionStepList>,
): boolean => {
  if (!canvasActionIdToActionComponent) {
    return false;
  }

  return Object.values(canvasActionIdToActionComponent).some(
    comp => comp.steps.includes(stepId) && comp.entry_step_id !== stepId,
  );
};

/**
 * Check if we can add a step after a given step.
 * Returns false if:
 * - The step is a branching step (buttons/condition/article feedback) with no branch selected
 * - The next step (if provided) is an internal action step
 *
 * Based on the legacy selectIsBranching selector from workflowBuilderReducer.
 */
export const canAddStepAfter = ({
  canvasActionIdToActionComponent,
  nextStepId,
  outputVariableValues,
  selectedActionCases,
  selectedConditions,
  step,
  stepId,
  stepMap,
}: {
  canvasActionIdToActionComponent?: Record<string, ActionStepList>;
  nextStepId?: string | null;
  outputVariableValues: Record<string, Record<string, string>>;
  selectedActionCases: Record<string, boolean>;
  selectedConditions: Record<string, number>;
  step: Step;
  stepId: string;
  stepMap: Record<string, Step>;
}): boolean => {
  // Don't allow adding step after this one if the next step is an internal action step
  // This prevents inserting steps between action entry and internal steps like "Let me find more ways to help you."
  if (
    nextStepId &&
    isInternalActionStep(nextStepId, canvasActionIdToActionComponent)
  ) {
    return false;
  }
  // For BUTTONS steps: Check if user has selected a button
  if (step.step_type === StepTypes.BUTTONS) {
    const stepFields = step.step_fields as ButtonsStepFields;
    const outputVariableValue =
      outputVariableValues[stepId]?.[stepFields.output_variable];

    // If there is no output variable value or output variable value is empty string,
    // that means that user has not selected any option - can't add step here
    return outputVariableValue !== undefined && outputVariableValue !== '';
  }

  if (step.step_type === StepTypes.MINI_AUTOFLOW_POLICY) {
    const stepFields = step.step_fields as MiniAutoflowPolicyStepFields;
    const outputVariableValue =
      outputVariableValues[stepId]?.[stepFields.output_context_variable];

    return outputVariableValue !== undefined && outputVariableValue !== '';
  }

  // For CONDITION steps: Check if user has selected a condition
  if (step.step_type === StepTypes.CONDITION) {
    return selectedConditions[stepId] !== undefined;
  }

  // For ARTICLE_SUGGESTION actions with feedback branches: Check if user has selected a branch
  // For GRANULAR_ARTICLE_SUGGESTION_V2: Check if the internal condition is selected
  // For DYNAMIC_CARD with fallback: Check if user has selected a branch
  if (canvasActionIdToActionComponent && stepMap) {
    const actionComponent = Object.values(canvasActionIdToActionComponent).find(
      comp => comp.entry_step_id === stepId,
    );

    if (actionComponent?.transition_branches?.length) {
      // Check if this is ARTICLE_SUGGESTION by looking for Yes/No display_name
      // ARTICLE_SUGGESTION uses "Yes" and "No" for feedback branches
      const isArticleSuggestionFeedback =
        actionComponent.transition_branches.some(
          branch =>
            branch.display_name === 'Yes' || branch.display_name === 'No',
        );

      // Check if this is DYNAMIC_CARD with fallback by looking for "Card Selected" display_name
      const isDynamicCardFallback = actionComponent.transition_branches.some(
        branch => branch.display_name === dynamicCardSelectedCase,
      );

      if (isArticleSuggestionFeedback) {
        // ARTICLE_SUGGESTION with Yes/No feedback - requires branch selection
        return selectedActionCases[stepId] !== undefined;
      } else if (isDynamicCardFallback) {
        // DYNAMIC_CARD with fallback - requires branch selection
        return selectedActionCases[stepId] !== undefined;
      } else {
        // GRANULAR_ARTICLE_SUGGESTION_V2 - check if internal condition is selected
        // Find the CONDITION step within this action
        const conditionStepId = actionComponent.steps.find(
          stepId => stepMap[stepId]?.step_type === StepTypes.CONDITION,
        );

        if (conditionStepId) {
          // Requires condition branch selection
          return selectedConditions[conditionStepId] !== undefined;
        }
      }
    }
  }

  // For all other step types, we can add after them
  return true;
};
